import { supabase } from '../supabaseClient';

/**
 * Service for the main-level (cross-project) Site Visit page.
 *
 * Unlike the Sustenance Site Visit page (which is scoped to a single project via
 * the `sitevisitsofleadsforsustenance` RPC), this page is scoped to a set of users
 * in the org hierarchy. It calls a sibling RPC `sitevisitsofleadshierarchy` that
 * takes an array of user ids and returns the SAME denormalized columns
 * (sitevisitid, leadname, leaddisplayid, sitevisiturl, sitevisitdate, billingname,
 * fosname, username, cpid, userid, projectname, inventorytype, email, number, status).
 */

// Sort options mirror getAllSiteVisits() in SustenanceService.js so the existing
// "Sort by" dropdown works unchanged.
const SiteVisitFilters = {
  'Name Ascending': { key: 'leadname', isAscending: true },
  'Name Descending': { key: 'leadname', isAscending: false },
  'Oldest First': { key: 'sitevisitdate', isAscending: true },
  'Latest First': { key: 'sitevisitdate', isAscending: false },
};

/**
 * Fetch site visits across all projects for the given hierarchy of user ids.
 * Pagination, sorting, search, CP / Sales / date filters mirror the Sustenance flow.
 */
async function getAllSiteVisitsByHierarchy(
  userIds,
  from,
  to,
  search,
  drpDownSelectedVal,
  selectedCp,
  selectedSales,
  selectDates,
  selectedDates,
  selectedProjects = [],
  selectedLeadQuality = [],
  selectedStatus = [],
  selectedVerification = [],
  selectedLeadType = [],
  selectedPendingWith = []
) {
  let query = supabase
    .rpc(
      'sitevisitsofleadshierarchy',
      { userids: userIds },
      { count: 'exact' }
    )
    .order(SiteVisitFilters[drpDownSelectedVal].key, {
      ascending: SiteVisitFilters[drpDownSelectedVal].isAscending,
    })
    .range(from, to);

  if (search !== null && search !== undefined && search !== '') {
    search = search.replace(/\s\s+/g, ' ');
    query = query.ilike('leadname', `%${search}%`);
  }

  if (selectedCp.length !== 0) {
    query = query.in('cpid', selectedCp);
  }

  if (selectedSales.length !== 0) {
    query = query.in('userid', selectedSales);
  }

  // Project / Lead Quality / Status filters — mirror the Leads tab. These rely on
  // projectid / leadresponse / leadstatus being returned by the RPC.
  if (selectedProjects.length !== 0) {
    query = query.in('projectid', selectedProjects);
  }

  if (selectedLeadQuality.length !== 0) {
    query = query.in('leadresponse', selectedLeadQuality);
  }

  if (selectedStatus.length !== 0) {
    query = query.in('leadstatus', selectedStatus);
  }

  // Verification filter: array of siteVisitVerificationStatus values
  // ('approved' | 'rejected' | 'pending'). Selecting one narrows to that state.
  if (selectedVerification.length !== 0) {
    query = query.in('sitevisitverificationstatus', selectedVerification);
  }

  // Lead Type filter (Direct / CP / R&L) — relies on leadtype being returned by the RPC.
  if (selectedLeadType.length !== 0) {
    query = query.in('leadtype', selectedLeadType);
  }

  // "Verification Pending With" filter: the approver(s) (userParentId). Shows ALL
  // site visits under the selected approver(s) — approved / rejected / pending.
  if (selectedPendingWith.length !== 0) {
    query = query.in('userparentid', selectedPendingWith);
  }

  if (selectedDates !== null) {
    query = query
      .gte('sitevisitdate', new Date(selectDates[0].value).getTime())
      .lte('sitevisitdate', new Date(selectDates[1].value).getTime());
  }

  const { data, count, error } = await query;
  if (data) return { data, count };
  else throw error;
}

/**
 * Marks a site visit as verified/approved (siteVisitVerificationStatus = 'approved').
 * Records the approver in verified_by/verified_at and the note in `comment`.
 * Allowed for the lead's mapped parent (Leads.userParentId) or anyone above them.
 * Note the table's primary key is spelled `siteVistId`.
 */
async function updateSiteVisitVerification(siteVistId, verifiedBy, verifiedAt, comment) {
  const { data, error } = await supabase
    .from('SiteVisits')
    .update({
      siteVisitVerificationStatus: 'approved',
      verified_by: verifiedBy,
      verified_at: verifiedAt,
      comment: comment,
    })
    .eq('siteVistId', siteVistId);
  if (error) throw error;
  return data;
}

/**
 * Reverts a site visit to the pending state (siteVisitVerificationStatus = 'pending')
 * and clears verified_by/verified_at. Currently unused (the Reject flow replaced it),
 * kept for completeness. Uses the new status column so it doesn't reference is_verified.
 */
async function updateSiteVisitUnverification(siteVistId, comment) {
  const { data, error } = await supabase
    .from('SiteVisits')
    .update({
      siteVisitVerificationStatus: 'pending',
      verified_by: null,
      verified_at: null,
      comment: comment,
    })
    .eq('siteVistId', siteVistId);
  if (error) throw error;
  return data;
}

/**
 * Rejects a site visit (siteVisitVerificationStatus = 'rejected'). Records the
 * rejecting user in verified_by / verified_at and the reason in `comment`.
 * Same approver eligibility as verify (gated in the UI via `canApprove`).
 */
async function updateSiteVisitRejection(siteVistId, rejectedBy, rejectedAt, comment) {
  const { data, error } = await supabase
    .from('SiteVisits')
    .update({
      siteVisitVerificationStatus: 'rejected',
      verified_by: rejectedBy,
      verified_at: rejectedAt,
      comment: comment,
    })
    .eq('siteVistId', siteVistId);
  if (error) throw error;
  return data;
}

/**
 * Returns the distinct approver user-ids that currently have at least one PENDING
 * site visit (within the given hierarchy scope), used to populate the
 * "Verification Pending With" filter. Names are resolved on the page via getUsersNameMap.
 */
async function getPendingApprovers(userIds) {
  const { data, error } = await supabase
    .rpc('sitevisitsofleadshierarchy', { userids: userIds })
    .eq('sitevisitverificationstatus', 'pending')
    .not('userparentid', 'is', null);
  if (error) throw error;
  const seen = new Set();
  const result = [];
  (data || []).forEach((r) => {
    if (r.userparentid && !seen.has(r.userparentid)) {
      seen.add(r.userparentid);
      result.push(r.userparentid);
    }
  });
  return result;
}

/**
 * Returns the users whose role is "Sales Manager" (resolved from the Roles table
 * by title), used to populate the Super Admin's Sales Manager dropdown.
 */
async function getSalesManagers() {
  const { data: roles, error: roleErr } = await supabase
    .from('Roles')
    .select('roleId, title')
    .ilike('title', 'Sales Manager');
  if (roleErr) throw roleErr;
  if (!roles || roles.length === 0) return [];

  const roleIds = roles.map((r) => r.roleId);
  const { data: users, error: userErr } = await supabase
    .from('Users')
    .select('userId, name, parentId, roleId')
    .in('roleId', roleIds);
  if (userErr) throw userErr;

  return (users || []).filter((u) => u.name);
}

/**
 * Returns [salesManagerUserId, ...all descendant user ids] by walking the
 * Users.parentId tree (breadth-first). Mirrors the getAllDescendantIds pattern
 * used in CallLogsMain / SalesManagerPersona.
 */
async function getDescendantUserIds(salesManagerUserId) {
  const { data: allUsers, error } = await supabase
    .from('Users')
    .select('userId, parentId');
  if (error) throw error;

  const childrenByParent = {};
  (allUsers || []).forEach((u) => {
    if (!childrenByParent[u.parentId]) childrenByParent[u.parentId] = [];
    childrenByParent[u.parentId].push(u.userId);
  });

  const result = [];
  const queue = [salesManagerUserId];
  while (queue.length) {
    const current = queue.shift();
    result.push(current);
    (childrenByParent[current] || []).forEach((childId) => queue.push(childId));
  }
  return result;
}

/**
 * Returns a { [userId]: name } map for all users, used to resolve the names of
 * the verifier (SiteVisits.verified_by) and the lead's designated approver
 * (Leads.userParentId) for display in the Site Visit Approval modal.
 */
async function getUsersNameMap() {
  const { data, error } = await supabase
    .from('Users')
    .select('userId, name');
  if (error) throw error;
  const map = {};
  (data || []).forEach((u) => {
    map[u.userId] = u.name;
  });
  return map;
}

export {
  getAllSiteVisitsByHierarchy,
  updateSiteVisitVerification,
  updateSiteVisitUnverification,
  updateSiteVisitRejection,
  getPendingApprovers,
  getSalesManagers,
  getDescendantUserIds,
  getUsersNameMap,
};
